// Admin (HR only): people, org chart, departments and job roles, settings.

import { useState, type FormEvent } from "react";
import { NavLink, Navigate, useParams } from "react-router";
import { REGIONS } from "../lib/bankholidays";
import { formatDays, leaveYearLabel, leaveYearOf, monthName } from "../lib/dates";
import { TYPE_LIST, balanceFor } from "../lib/leave";
import { aboveInTeam, managerIds, teamHeads } from "../lib/orgchart";
import type { UserPatch } from "../store/base";
import { useStore } from "../store/StoreProvider";
import { useCtx, type Ctx } from "../store/useCtx";
import type { Region, Role, User } from "../types";
import { PersonDialog } from "../dialogs/PersonDialog";
import { JobRoles, OrgChart } from "./OrgChart";
import { Avatar, EmployeeName, byName, plural, typeStyle } from "../ui/bits";
import { CommitInput } from "../ui/CommitInput";
import { useDialogs } from "../ui/dialogs";
import { Icon } from "../ui/Icon";
import { useRun, useToast } from "../ui/Toasts";

const TABS = [
  { id: "people", label: "People" },
  { id: "org-chart", label: "Org chart" },
  { id: "departments", label: "Dep and job roles" },
  { id: "settings", label: "Settings" },
] as const;
type TabId = (typeof TABS)[number]["id"];
type PeopleSort =
  | "role"
  | "department"
  | "name"
  | "email"
  | "jobRole"
  | "reportsTo"
  | "access"
  | "allowance"
  | "remaining"
  | "active";

const roleRank = (user: User) => (user.role === "hr" ? 0 : user.isProductManager ? 1 : user.isTeamLead ? 2 : 3);

export function Admin() {
  const { tab } = useParams();
  const ctx = useCtx();
  // Which person's dialog is open: none, a new person, or an existing one being edited.
  const [dialog, setDialog] = useState<{ person?: User } | null>(null);
  if (tab === "job-roles") return <Navigate to="/admin/departments" replace />;
  if (!TABS.some((t) => t.id === tab)) return <Navigate to="/admin/people" replace />;
  const edit = (person?: User) => setDialog({ person });
  return (
    <>
      <nav className="tabs" aria-label="Admin sections">
        {TABS.map((t) => (
          <NavLink key={t.id} className="tab" to={`/admin/${t.id}`}>
            {t.label}
          </NavLink>
        ))}
      </nav>
      <div>
        {(tab as TabId) === "people" ? (
          <PeopleTab ctx={ctx} onEdit={edit} />
        ) : (tab as TabId) === "org-chart" ? (
          <OrgChart ctx={ctx} onEdit={edit} />
        ) : (tab as TabId) === "departments" ? (
          <>
            <h2 className="section-title">Departments</h2>
            <DepartmentsTab ctx={ctx} />
            <JobRoles ctx={ctx} />
          </>
        ) : (
          <SettingsTab ctx={ctx} />
        )}
      </div>
      {dialog && (
        <PersonDialog key={dialog.person?.id ?? "new"} person={dialog.person} onClose={() => setDialog(null)} />
      )}
    </>
  );
}

function PeopleTab({ ctx, onEdit }: { ctx: Ctx; onEdit: (person?: User) => void }) {
  const store = useStore();
  const run = useRun();
  const toast = useToast();
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<PeopleSort>("role");
  const [descending, setDescending] = useState(false);
  const ly = leaveYearOf(ctx.today, ctx.settings.leaveYearStartMonth);
  const departmentNames = new Map(ctx.departments.map((department) => [department.id, department.name]));
  const departmentName = (user: User) => departmentNames.get(user.departmentId ?? "") ?? "No department";
  const departmentQuery = departmentFilter.trim().toLowerCase();
  const emailQuery = emailFilter.trim().toLowerCase();
  const managers = managerIds(ctx.users);
  const jobRoleNames = new Map(ctx.jobRoles.map((jobRole) => [jobRole.id, jobRole.name]));
  const reportsToName = (user: User) =>
    user.role === "hr" ? "Not needed" : (ctx.userById.get(managers.get(user.id) ?? "")?.name ?? "HR");
  const comparisons: Record<PeopleSort, (first: User, second: User) => number> = {
    role: (first, second) =>
      roleRank(first) - roleRank(second) || departmentName(first).localeCompare(departmentName(second)),
    department: (first, second) =>
      departmentName(first).localeCompare(departmentName(second)) || roleRank(first) - roleRank(second),
    name: byName,
    email: (first, second) => first.email.localeCompare(second.email),
    jobRole: (first, second) =>
      (jobRoleNames.get(first.jobRoleId ?? "") ?? "No job role").localeCompare(
        jobRoleNames.get(second.jobRoleId ?? "") ?? "No job role",
      ),
    reportsTo: (first, second) => reportsToName(first).localeCompare(reportsToName(second)),
    access: (first, second) => first.role.localeCompare(second.role),
    allowance: (first, second) =>
      (first.allowance ?? ctx.settings.defaultAllowance) - (second.allowance ?? ctx.settings.defaultAllowance),
    remaining: (first, second) => balanceFor(first, ly, ctx).remaining - balanceFor(second, ly, ctx).remaining,
    active: (first, second) => Number(first.active) - Number(second.active),
  };
  const users = ctx.users
    .filter(
      (user) =>
        departmentName(user).toLowerCase().includes(departmentQuery) && user.email.toLowerCase().includes(emailQuery),
    )
    .sort((first, second) => {
      const order = comparisons[sortBy](first, second);
      return (descending ? -1 : 1) * (order || Number(second.active) - Number(first.active) || byName(first, second));
    });
  const heads = teamHeads(ctx.users);
  const reportsToChoices = ctx.users.filter((u) => u.active && u.role !== "hr").sort(byName);
  const sortButton = (column: PeopleSort, label: string) => (
    <button
      type="button"
      className="link-btn people-sort"
      aria-label={`Sort by ${label}`}
      onClick={() => {
        setDescending(sortBy === column ? !descending : false);
        setSortBy(column);
      }}
    >
      {label}
      <span className="people-sort-direction" aria-hidden="true">
        {sortBy === column ? (descending ? "DESC" : "ASC") : ""}
      </span>
    </button>
  );
  const sortHeader = (column: PeopleSort, label: string) => (
    <th
      scope="col"
      aria-label={label}
      aria-sort={sortBy === column ? (descending ? "descending" : "ascending") : undefined}
    >
      {sortButton(column, label)}
    </th>
  );

  /** Saves one change to one person. Resolves to whether it went through. */
  const save = async (user: User, patch: UserPatch): Promise<boolean> => {
    const otherHR = ctx.users.some((u) => u.id !== user.id && u.role === "hr" && u.active);
    const losesHR = ("role" in patch && patch.role !== "hr") || ("active" in patch && !patch.active);
    if (user.id === ctx.me.id && !otherHR && losesHR) {
      toast(
        "You are the only active HR user, so you cannot remove your own HR access or deactivate yourself.",
        "error",
      );
      return false;
    }
    return run(store.saveUser(user.id, patch), `${user.name} updated.`);
  };

  const saveAllowance = (user: User, text: string): Promise<boolean> | boolean => {
    if (text.trim() === "") return save(user, { allowance: null });
    const value = Number(text);
    if (!Number.isFinite(value) || value < 0 || value > 366) {
      toast("Allowance must be between 0 and 366 days.", "error");
      return false;
    }
    return save(user, { allowance: value });
  };

  return (
    <>
      <div className="people-head">
        <p className="hint">
          Changes save as soon as you make them. Tick <strong>Team lead</strong> or <strong>Product manager</strong> for
          one person in each team: everyone else in the team reports to the team lead (or the product manager if there
          is no team lead), and the team lead reports to the product manager. Whoever someone reports to can approve
          their leave; HR can approve anyone's. Leave <strong>Allowance</strong> blank to use the company default of{" "}
          <strong>{formatDays(ctx.settings.defaultAllowance)} days</strong>.
        </p>
        <button type="button" className="btn" onClick={() => onEdit()}>
          <Icon name="plus" size={18} />
          Add someone
        </button>
      </div>
      <button
        type="button"
        className="btn secondary"
        aria-expanded={filtersOpen}
        aria-controls="people-filters"
        onClick={() => setFiltersOpen(!filtersOpen)}
      >
        <Icon name="sliders" size={18} />
        Filter{departmentQuery || emailQuery ? " (active)" : ""}
      </button>
      <div id="people-filters" className="people-controls" hidden={!filtersOpen}>
        <div className="field">
          <label htmlFor="people-department-filter">Filter by department</label>
          <input
            id="people-department-filter"
            type="search"
            value={departmentFilter}
            onChange={(event) => setDepartmentFilter(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="people-email-filter">Filter by email</label>
          <input
            id="people-email-filter"
            type="search"
            value={emailFilter}
            onChange={(event) => setEmailFilter(event.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn secondary"
          disabled={!departmentFilter && !emailFilter}
          onClick={() => {
            setDepartmentFilter("");
            setEmailFilter("");
          }}
        >
          Clear filters
        </button>
      </div>
      <p className="hint" role="status">
        {users.length} of {ctx.users.length} people
      </p>
      <div className="card table-wrap">
        <table className="table table-admin">
          <thead>
            <tr>
              <th
                scope="col"
                aria-sort={
                  sortBy === "name" || sortBy === "email" ? (descending ? "descending" : "ascending") : undefined
                }
              >
                {sortButton("name", "Person")}
                {sortButton("email", "Email")}
              </th>
              {sortHeader("department", "Department")}
              {sortHeader("jobRole", "Job role")}
              {sortHeader("role", "Team role")}
              {sortHeader("reportsTo", "Reports to")}
              {sortHeader("access", "Access")}
              {sortHeader("allowance", "Allowance (days)")}
              {sortHeader("remaining", `Left in ${leaveYearLabel(ly, ctx.settings.leaveYearStartMonth)}`)}
              {sortHeader("active", "Active")}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={9}>No people match these filters.</td>
              </tr>
            )}
            {users.map((u) => {
              const bal = balanceFor(u, ly, ctx);
              return (
                <tr key={u.id} className={u.active ? "" : "inactive"}>
                  <th scope="row">
                    <div className="employee-identity">
                      <Avatar name={u.name} photoUrl={u.photoUrl} />
                      <div>
                        <span className="pname">{u.name}</span>
                        <span className="muted block">{u.email}</span>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => onEdit(u)}
                          aria-label={`Edit details for ${u.name}`}
                        >
                          Edit details
                        </button>
                      </div>
                    </div>
                  </th>
                  <td>
                    <label className="sr-only" htmlFor={`dept-${u.id}`}>
                      Department for {u.name}
                    </label>
                    <select
                      id={`dept-${u.id}`}
                      value={u.departmentId ?? ""}
                      onChange={(e) => save(u, { departmentId: e.target.value || null })}
                    >
                      <option value="">No department</option>
                      {ctx.departments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <label className="sr-only" htmlFor={`job-${u.id}`}>
                      Job role for {u.name}
                    </label>
                    <select
                      id={`job-${u.id}`}
                      value={u.jobRoleId ?? ""}
                      onChange={(e) => save(u, { jobRoleId: e.target.value || null })}
                    >
                      <option value="">No job role</option>
                      {ctx.jobRoles.map((j) => (
                        <option key={j.id} value={j.id}>
                          {j.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {u.role === "hr" ? (
                      <span
                        className="muted"
                        title="HR approve everyone's leave, so they are not part of a team's chart"
                      >
                        Not needed
                      </span>
                    ) : (
                      <div className="ticks">
                        <label className="tick">
                          <input
                            type="checkbox"
                            checked={u.isTeamLead}
                            aria-label={`Team lead: ${u.name}`}
                            onChange={(e) => save(u, { isTeamLead: e.target.checked })}
                          />{" "}
                          Team lead
                        </label>
                        <label className="tick">
                          <input
                            type="checkbox"
                            checked={u.isProductManager}
                            aria-label={`Product manager: ${u.name}`}
                            onChange={(e) => save(u, { isProductManager: e.target.checked })}
                          />{" "}
                          Product manager
                        </label>
                      </div>
                    )}
                  </td>
                  <td>
                    {u.role === "hr" ? (
                      <span className="muted">Not needed</span>
                    ) : aboveInTeam(u, heads) ? (
                      <span title="Worked out from the team">
                        <EmployeeName user={ctx.userById.get(managers.get(u.id)!)} />
                      </span>
                    ) : (
                      <>
                        <label className="sr-only" htmlFor={`reports-${u.id}`}>
                          Reports to for {u.name}
                        </label>
                        <select
                          id={`reports-${u.id}`}
                          value={u.reportsToId ?? ""}
                          onChange={(e) => save(u, { reportsToId: e.target.value || null })}
                        >
                          <option value="">HR</option>
                          {reportsToChoices
                            .filter((l) => l.id !== u.id)
                            .map((l) => (
                              <option key={l.id} value={l.id}>
                                {l.name}
                              </option>
                            ))}
                        </select>
                      </>
                    )}
                  </td>
                  <td>
                    <label className="sr-only" htmlFor={`role-${u.id}`}>
                      Role for {u.name}
                    </label>
                    <select
                      id={`role-${u.id}`}
                      value={u.role}
                      onChange={(e) => save(u, { role: e.target.value as Role })}
                    >
                      <option value="employee">Employee</option>
                      <option value="hr">HR</option>
                    </select>
                  </td>
                  <td>
                    <label className="sr-only" htmlFor={`allow-${u.id}`}>
                      Yearly allowance for {u.name}
                    </label>
                    <CommitInput
                      id={`allow-${u.id}`}
                      className="num"
                      type="number"
                      min={0}
                      max={366}
                      step={0.5}
                      inputMode="decimal"
                      placeholder={formatDays(ctx.settings.defaultAllowance)}
                      value={typeof u.allowance === "number" ? String(u.allowance) : ""}
                      onCommit={(text) => saveAllowance(u, text)}
                    />
                  </td>
                  <td className={bal.remaining < 0 ? "text-warn" : ""}>
                    {formatDays(bal.remaining)} <span className="muted">of {formatDays(bal.allowance)}</span>
                  </td>
                  <td>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={u.active}
                        onChange={(e) => save(u, { active: e.target.checked })}
                      />
                      <span className="switch-track" aria-hidden="true" />
                      <span className="sr-only">{u.name} is active</span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="hint">
        Deactivating someone stops them signing in and hides them from the calendar; their past leave is kept. Anyone
        who is not on this list sees a message asking them to contact their team lead or HR.
      </p>
    </>
  );
}

function DepartmentsTab({ ctx }: { ctx: Ctx }) {
  const store = useStore();
  const run = useRun();
  const toast = useToast();
  const { confirm } = useDialogs();
  const [newName, setNewName] = useState("");
  const heads = teamHeads(ctx.users);
  const depts = ctx.departments.slice().sort((a, b) => a.name.localeCompare(b.name, "en-GB"));

  const remove = async (id: string) => {
    const d = ctx.deptById.get(id)!;
    const members = ctx.users.filter((u) => u.departmentId === id);
    if (members.length) {
      toast(`Move ${plural(members.length, "person", "people")} out of ${d.name} first (People tab).`, "error");
      return;
    }
    const ok = await confirm({
      title: `Delete ${d.name}?`,
      message: "This cannot be undone.",
      confirmLabel: "Delete department",
      danger: true,
    });
    if (ok) await run(store.deleteDepartment(id), "Department deleted.");
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (await run(store.saveDepartment({ name }), `${name} added.`)) setNewName("");
  };

  return (
    <>
      <p className="hint">
        A department is a team. It groups people on the team calendar and decides who reports to whom: tick one person
        as its
        <strong> team lead</strong> and one as its <strong>product manager</strong> on the People tab, and everyone else
        in the team reports to them automatically. HR can approve anyone's leave.
      </p>
      <ul className="stack">
        {depts.map((d) => {
          const members = ctx.users.filter((u) => u.departmentId === d.id && u.active);
          return (
            <li key={d.id} className="card dept">
              <div className="dept-head">
                <div className="field grow">
                  <label htmlFor={`dname-${d.id}`}>Department name</label>
                  <CommitInput
                    id={`dname-${d.id}`}
                    type="text"
                    maxLength={60}
                    value={d.name}
                    onCommit={(text) => {
                      const name = text.trim();
                      if (!name) {
                        toast("A department needs a name.", "error");
                        return false;
                      }
                      return run(store.saveDepartment({ id: d.id, name }), "Department renamed.");
                    }}
                  />
                </div>
                <button type="button" className="btn btn-ghost-danger btn-sm" onClick={() => remove(d.id)}>
                  <Icon name="trash" size={16} />
                  Delete
                </button>
              </div>
              <p className="muted">
                Team lead: <strong>{ctx.userById.get(heads.lead.get(d.id) ?? "")?.name ?? "none"}</strong>
                {" · "}Product manager: <strong>{ctx.userById.get(heads.pm.get(d.id) ?? "")?.name ?? "none"}</strong>
              </p>
              <p className="muted">{plural(members.length, "person", "people")} in this department</p>
            </li>
          );
        })}
      </ul>
      <form className="card add-dept" onSubmit={add}>
        <div className="field grow">
          <label htmlFor="new-dept">Add a department</label>
          <input
            id="new-dept"
            type="text"
            maxLength={60}
            placeholder="e.g. Finance"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
        <button type="submit" className="btn">
          <Icon name="plus" size={18} />
          Add
        </button>
      </form>
    </>
  );
}

function SettingsTab({ ctx }: { ctx: Ctx }) {
  const store = useStore();
  const run = useRun();
  const toast = useToast();
  const s = ctx.settings;

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const defaultAllowance = Number(f.get("defaultAllowance"));
    if (!Number.isFinite(defaultAllowance) || defaultAllowance < 0 || defaultAllowance > 366) {
      toast("Default allowance must be between 0 and 366 days.", "error");
      return;
    }
    await run(
      store.saveSettings({
        defaultAllowance,
        leaveYearStartMonth: Number(f.get("leaveYearStartMonth")),
        region: f.get("region") as Region,
      }),
      "Settings saved.",
    );
  };

  return (
    <>
      {/* Keyed by the saved values, so the form shows what was actually saved. */}
      <form
        key={`${s.defaultAllowance}-${s.leaveYearStartMonth}-${s.region}`}
        className="card settings"
        onSubmit={save}
      >
        <div className="field">
          <label htmlFor="s-allow">Default yearly allowance (days)</label>
          <input
            id="s-allow"
            name="defaultAllowance"
            className="num"
            type="number"
            min={0}
            max={366}
            step={0.5}
            inputMode="decimal"
            defaultValue={s.defaultAllowance}
            required
          />
          <p className="hint">
            Applies to everyone who does not have their own allowance set on the People tab. UK statutory minimum is 28
            days including bank holidays (5.6 weeks).
          </p>
        </div>
        <div className="field">
          <label htmlFor="s-month">Leave year starts in</label>
          <select id="s-month" name="leaveYearStartMonth" defaultValue={s.leaveYearStartMonth}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={i + 1}>
                {monthName(i + 1)}
              </option>
            ))}
          </select>
          <p className="hint">
            Balances are calculated per leave year. Changing this changes how everyone's remaining days are worked out.
          </p>
        </div>
        <div className="field">
          <label htmlFor="s-region">Bank holidays for</label>
          <select id="s-region" name="region" defaultValue={s.region}>
            {Object.entries(REGIONS).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          <p className="hint">
            Bank holidays are excluded from leave and do not use up allowance. Dates are built in up to the end of 2028.
          </p>
        </div>
        <div>
          <button type="submit" className="btn">
            Save settings
          </button>
        </div>
      </form>

      <h2 className="section-title">Leave types</h2>
      <div className="card table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col">Needs approval</th>
              <th scope="col">Uses holiday allowance</th>
            </tr>
          </thead>
          <tbody>
            {TYPE_LIST.map((t) => (
              <tr key={t.id}>
                <td>
                  <span className="type-dot inline" style={typeStyle(t)}>
                    <Icon name={t.icon} size={16} />
                  </span>{" "}
                  {t.label}
                </td>
                <td>{t.needsApproval ? "Yes" : "No, recorded immediately"}</td>
                <td>{t.deducts ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
